import { ComponentType, useState } from 'react';
import { AMENITY_LABELS } from 'constants/venues/profileOptions';
import { AmenitiesEnum } from 'types/generated/server';
import Check from 'svg/Check';
import ChevronDown from 'svg/ChevronDown';
import ChevronUp from 'svg/ChevronUp';
import { AMENITIES_PREVIEW_COUNT, AMENITY_CONFIG } from '../../constants';

interface Props {
  amenities: AmenitiesEnum[];
  customAmenities?: string[];
}

interface AmenityRow {
  key: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}

export default function AmenitiesList({ amenities, customAmenities = [] }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Map every amenity to a label (from the shared source) and an icon (specific
  // where we have one, otherwise a neutral check). Custom amenities append last.
  const rows: AmenityRow[] = [
    ...amenities.map((amenity) => ({
      key: amenity,
      label: AMENITY_LABELS[amenity] ?? amenity,
      Icon: AMENITY_CONFIG[amenity] ?? Check,
    })),
    ...customAmenities.map((name) => ({
      key: `custom-${name}`,
      label: name,
      Icon: Check,
    })),
  ];

  if (rows.length === 0) {
    return null;
  }

  const visible = isExpanded ? rows : rows.slice(0, AMENITIES_PREVIEW_COUNT);
  const canToggle = rows.length > AMENITIES_PREVIEW_COUNT;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        Amenities:
      </h3>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {visible.map(({ key, label, Icon }) => (
          <div key={key} className="flex items-center gap-2">
            <Icon className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
            <span className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {label}
            </span>
          </div>
        ))}
      </div>

      {canToggle && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="typography-product-button-label-large flex items-center gap-1.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
        >
          {isExpanded ? 'Show less' : 'Show all'}
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      )}
    </div>
  );
}
